import {
	CloudSyncOutlined,
	FilterOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Empty,
	Input,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { getAllSalesPrices } from "../../services/salesPriceService";
import { syncSalesPrices } from "../../services/salesPriceSyncService";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { CustomFilterInput } from "../Textfield/CustomFilterInput";

const { Title } = Typography;
const { Option } = Select;

const SalesPriceList = () => {
	const [salesPrices, setSalesPrices] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filteredData, setFilteredData] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [filterSalesType, setFilterSalesType] = useState("");
	const [filterCurrency, setFilterCurrency] = useState("");
	const isMobile = window.innerWidth < 768;

	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	const { country } = useConfig();

	// Cargar precios de venta al inicio
	useEffect(() => {
		loadSalesPrices();
	}, []);

	const filterData = useCallback(() => {
		let filtered = [...salesPrices];

		// Filtro por texto de búsqueda
		if (searchText) {
			filtered = filtered.filter(
				(item) =>
					item.Item_No?.toLowerCase().includes(searchText.toLowerCase()) ||
					item.Sales_Code?.toLowerCase().includes(searchText.toLowerCase()) ||
					item.Unit_of_Measure_Code?.toLowerCase().includes(
						searchText.toLowerCase()
					) ||
					item.Variant_Code?.toLowerCase().includes(searchText.toLowerCase())
			);
		}

		// Filtro por tipo de venta
		if (filterSalesType) {
			filtered = filtered.filter((item) => item.Sales_Type === filterSalesType);
		}

		// Filtro por moneda
		if (filterCurrency) {
			filtered = filtered.filter(
				(item) => item.Currency_Code === filterCurrency
			);
		}

		setFilteredData(filtered);
	}, [salesPrices, searchText, filterSalesType, filterCurrency]);

	// Filtrar datos cuando cambien los filtros
	useEffect(() => {
		filterData();
	}, [filterData]);

	const loadSalesPrices = async () => {
		try {
			console.log("Cargando precios de venta...");
			setLoading(true);
			const priceList = await getAllSalesPrices();
			setSalesPrices(priceList);
		} catch (error) {
			window.messageApi.error(
				"Error al cargar precios de venta: " + error.message
			);
		} finally {
			setLoading(false);
		}
	};

	const clearFilters = () => {
		setSearchText("");
		setFilterSalesType("");
		setFilterCurrency("");
	};

	const handleSyncSalesPrices = async () => {
		try {
			setLoading(true);
			const syncResult = await syncSalesPrices();
			await loadSalesPrices();
			if (syncResult) {
				window.messageApi.success("Precios de venta sincronizados correctamente");
			} else {
				window.messageApi.warning("No se pudo sincronizar precios de venta");
			}
		} catch (error) {
			window.messageApi.error(
				"Error al sincronizar precios de venta: " + error.message
			);
		} finally {
			setLoading(false);
		}
	};

	// Obtener valores únicos para los filtros
	const uniqueSalesTypes = [
		...new Set(
			salesPrices
				.map(item => item.Sales_Type)
				.filter(Boolean)
		)
	].map(type => ({
		text: type,
		value: type,
	}));

	const uniqueCurrencies = [
		...new Set(salesPrices.map((item) => item.Currency_Code).filter(Boolean)),
	].map(type => ({
		text: type,
		value: type,
	}));

	const uniqueSalesCodes = [
		...new Set(salesPrices.map((item) => item.Sales_Code).filter(Boolean)),
	].map(type => ({
		text: type,
		value: type,
	}));

	// Formatear fecha
	const formatDate = (dateString) => {
		if (!dateString) return "-";
		try {
			return new Date(dateString).toISOString().split('T')[0];
		} catch {
			return dateString;
		}
	};

	const columns = [
		{
			title: "No. Artículo",
			dataIndex: "Item_No",
			key: "Item_No",
			width: 120,
			fixed: isMobile ? false : "left",
			filterDropdown: (props) => <CustomFilterInput {...props} />,
			onFilter: (value, record) => record.Item_No?.toLowerCase().includes(value.toLowerCase()),
			sorter: (a, b) => {
				const No_a = a.Item_No ?? "";
				const No_b = b.Item_No ?? "";
				return No_a.toString().localeCompare(No_b.toString());
			},
			render: (text) => text || "-",
		},
		{
			title: "Tipo de Venta",
			dataIndex: "Sales_Type",
			key: "Sales_Type",
			width: 120,
			filters: uniqueSalesTypes,
			filterSearch: true,
			onFilter: (value, record) => record.Sales_Type === value,
			render: (text) => text || "-",
		},
		{
			title: "Código de Venta",
			dataIndex: "Sales_Code",
			key: "Sales_Code",
			width: 120,
			filters: uniqueSalesCodes,
			filterSearch: true,
			onFilter: (value, record) => record.Sales_Code === value,
			render: (text) => text || "-",
		},
		{
			title: "Fecha Inicio",
			dataIndex: "Starting_Date",
			key: "Starting_Date",
			width: 110,
			sorter: (a, b) => new Date(a.Starting_Date || 0) - new Date(b.Starting_Date || 0),
			render: (date) => formatDate(date),
		},
		{
			title: "Fecha Fin",
			dataIndex: "Ending_Date",
			key: "Ending_Date",
			width: 110,
			sorter: (a, b) => new Date(a.Ending_Date || 0) - new Date(b.Ending_Date || 0),
			render: (date) => formatDate(date),
		},
		{
			title: "Moneda",
			dataIndex: "Currency_Code",
			key: "Currency_Code",
			width: 80,
			filters: uniqueCurrencies,
			filterSearch: true,
			onFilter: (value, record) => record.Currency_Code === value,
			render: (currency) =>
				currency ? (
					<Tag
						color={
							currency === "EUR"
								? "blue"
								: currency === "USD"
									? "green"
									: "orange"
						}
					>
						{currency}
					</Tag>
				) : (
					"-"
				),
		},
		{
			title: "Precio Unitario",
			dataIndex: "Unit_Price",
			key: "Unit_Price",
			width: 120,
			filterDropdown: (props) => <CustomFilterInput {...props} />,
			onFilter: (value, record) => {
				if (record.Unit_Price == null) return false;
				const priceStr = String(record.Unit_Price);
				const filterStr = String(value).toLowerCase();
				return priceStr.toLowerCase().includes(filterStr);
			},
			sorter: (a, b) => {
				const PriceA = a.Unit_Price ?? 0;
				const PriceB = b.Unit_Price ?? 0;
				return PriceA - PriceB;
			},
			render: (price, record) => price ? formatNumberByCountry(price, country, true) : '-',
		},
		{
			title: "Cantidad Mínima",
			dataIndex: "Minimum_Quantity",
			key: "Minimum_Quantity",
			width: 120,
			render: (quantity) =>
				quantity !== null && quantity !== undefined
					? Number(quantity).toFixed(2)
					: "-",
		},
		{
			title: "Unidad de Medida",
			dataIndex: "Unit_of_Measure_Code",
			key: "Unit_of_Measure_Code",
			width: 130,
			render: (text) => text || "-",
		},
		{
			title: "Código Variante",
			dataIndex: "Variant_Code",
			key: "Variant_Code",
			width: 120,
			render: (text) => text || "-",
		},
		{
			title: "Desc. Línea",
			dataIndex: "Allow_Line_Disc",
			key: "Allow_Line_Disc",
			width: 100,
			render: (allow) => (
				<Tag color={allow ? "green" : "red"}>{allow ? "Sí" : "No"}</Tag>
			),
		},
		{
			title: "Desc. Factura",
			dataIndex: "Allow_Invoice_Disc",
			key: "Allow_Invoice_Disc",
			width: 110,
			render: (allow) => (
				<Tag color={allow ? "green" : "red"}>{allow ? "Sí" : "No"}</Tag>
			),
		},
		{
			title: "Grupo IVA",
			dataIndex: "VAT_Bus_Posting_Gr_Price",
			key: "VAT_Bus_Posting_Gr_Price",
			width: 120,
			render: (text) => text || "-",
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
				<p style={{ marginTop: "16px" }}>Cargando precios de venta...</p>
			</div>
		);
	}

	return (
		<div style={{ padding: isMobile ? "16px" : "24px" }}>
			<Card>
				<Row
					justify="space-between"
					align="middle"
					style={{ marginBottom: "24px" }}
				>
					<Col>
						<Title level={3} style={{ margin: 0 }}>
							Precios de Venta
						</Title>
					</Col>
					<Col>
						<Space>
							<Tooltip title="Actualizar lista">
								<Button
									icon={<CloudSyncOutlined />}
									onClick={handleSyncSalesPrices}
									loading={loading}
									type="primary"
								>
									Sincronizar precios de venta
								</Button>
							</Tooltip>
						</Space>
					</Col>
				</Row>

				{/* Filtros */}
				<Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
					<Col xs={24} sm={12} md={8} lg={6}>
						<Input
							placeholder="Buscar por artículo, código..."
							prefix={<SearchOutlined />}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							allowClear
						/>
					</Col>
				</Row>

				{/* Tabla */}
				<Table
					columns={columns}
					dataSource={filteredData}
					rowKey={(record, index) =>
						`${record.Item_No}-${record.Sales_Type}-${record.Sales_Code}-${index}`
					}
					pagination={{
						...pagination,
						showSizeChanger: true,
						showQuickJumper: true,
						pageSizeOptions: ["5", "10", "20", "50"],
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} de ${total} elementos`,
					}}
					scroll={{ x: 1400, y: isMobile ? 400 : 600 }}
					locale={{
						emptyText: (
							<Empty
								description="No hay precios de venta disponibles"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						),
					}}
					size={isMobile ? "small" : "middle"}
					onChange={(newPagination) => setPagination(newPagination)}
				/>

				{/* Información adicional */}
				<div style={{ marginTop: "16px", color: "#666" }}>
					<p>
						Total de precios: {filteredData.length}
						{filteredData.length !== salesPrices.length &&
							` (filtrados de ${salesPrices.length})`}
					</p>
				</div>
			</Card>
		</div>
	);
};

export default SalesPriceList;
